import React, { useEffect, useState } from 'react';
import {
  Box, Button, Typography, Paper, List, ListItem,
  ListItemIcon, ListItemText, CircularProgress, Alert,
} from '@mui/material';
import GitHubIcon from '@mui/icons-material/GitHub';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { loginSuccess, logout } from '../../store/slices/authSlice';
import { RootState } from '../../store/store';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8009';

const PERMISSIONS = [
  'Read your repository contents (to run analysis)',
  'Read repository metadata (name, language, branches)',
  'Write pull requests (to raise AI-generated fixes)',
];

export const GitHubInstallPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { accessToken, user } = useSelector((state: RootState) => state.auth);
  const [lookingUp, setLookingUp] = useState(false);
  const [error, setError] = useState('');

  // github_login is only needed for the lookup path (I've already installed it).
  // The main install flow works regardless — installation_id comes from the
  // redirect URL after the GitHub App is installed.
  const githubLinked = !!user?.github_login;

  // On mount: silently check for an existing installation if GitHub is linked.
  // Handles the case where the app was installed but installation_id wasn't saved.
  useEffect(() => {
    if (!accessToken) {
      navigate('/auth/login', { replace: true });
      return;
    }
    if (githubLinked) {
      tryLookup(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, githubLinked]);

  async function tryLookup(silent = false) {
    if (!silent) setLookingUp(true);
    setError('');

    try {
      const res = await fetch(`${API_BASE_URL}/auth/installation/lookup`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (res.status === 401) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('user');
        dispatch(logout());
        navigate('/auth/login', { replace: true });
        return;
      }

      if (res.ok) {
        const updatedUser = await res.json();
        localStorage.setItem('user', JSON.stringify(updatedUser));
        dispatch(loginSuccess({ user: updatedUser, accessToken: accessToken! }));
        navigate('/dashboard', { replace: true });
        return;
      }

      // 404 = not installed yet — show the install page
    } catch {
      if (!silent) setError('Could not reach the server. Please try again.');
    } finally {
      setLookingUp(false);
    }
  }

  // Step 1: link GitHub account via OAuth
  const handleConnectGitHub = () => {
    window.location.href = `${API_BASE_URL}/auth/github`;
  };

  // Step 2: install the GitHub App
  const handleInstall = () => {
    window.location.href = `${API_BASE_URL}/auth/install`;
  };

  if (lookingUp) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
        <CircularProgress size={24} />
        <Typography color="text.secondary">Checking GitHub App status…</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.50' }}>
      <Paper
        elevation={0}
        sx={{ p: 6, maxWidth: 480, width: '100%', borderRadius: 3, border: '1px solid', borderColor: 'grey.200' }}
      >
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <GitHubIcon sx={{ fontSize: 48, color: '#24292f', mb: 2 }} />
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Install the GitHub App
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Install the KodeGauge GitHub App to grant access to the repos you want to analyse.
          </Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          Permissions requested:
        </Typography>
        <List dense disablePadding sx={{ mb: 4 }}>
          {PERMISSIONS.map((perm) => (
            <ListItem key={perm} disableGutters>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <CheckCircleOutlineIcon fontSize="small" color="success" />
              </ListItemIcon>
              <ListItemText primary={perm} primaryTypographyProps={{ variant: 'body2' }} />
            </ListItem>
          ))}
        </List>

        <Button
          fullWidth
          size="large"
          variant="contained"
          startIcon={<GitHubIcon />}
          onClick={handleInstall}
          sx={{
            py: 1.5, bgcolor: '#24292f', '&:hover': { bgcolor: '#32383f' },
            textTransform: 'none', fontSize: '1rem', fontWeight: 600,
          }}
        >
          Install GitHub App
        </Button>

        {/* Lookup only works if GitHub account is linked via OAuth */}
        {githubLinked && (
          <Button
            fullWidth
            size="large"
            variant="outlined"
            onClick={() => tryLookup(false)}
            disabled={lookingUp}
            sx={{ mt: 1.5, textTransform: 'none' }}
          >
            {lookingUp ? 'Checking…' : "I've already installed it"}
          </Button>
        )}

        {!githubLinked && (
          <Button
            fullWidth
            size="small"
            variant="text"
            onClick={handleConnectGitHub}
            sx={{ mt: 1.5, textTransform: 'none', color: 'text.secondary' }}
          >
            Already installed? Connect your GitHub account to detect it
          </Button>
        )}
      </Paper>
    </Box>
  );
};
